/**
 * Сохраняемое состояние и его сериализация.
 *
 * Раньше снимок для сервера собирался белым списком полей в `toPayload`.
 * Список молча отставал от модели: новое поле в состоянии в снимок не
 * попадало, и старый клиент, записав снимок обратно, обеднял его без всякого
 * конфликта — сервер сверял только номер редакции.
 *
 * Здесь два разделения, которые это закрывают:
 *
 * 1. `PersistedState` — только факты и решения человека. Вычисляемое
 *    (`today`, `isEmpty`) живёт в `RuntimeState` и на сервер не уезжает:
 *    чужое «сегодня» испортило бы первый же расчёт на другом устройстве.
 * 2. Таблица кодеков обязана содержать **каждый** ключ `PersistedState`.
 *    Забытое поле — ошибка компиляции, а не потерянные данные пользователя.
 */
import { ZERO, amountFromLegacy, confirmed, unknownAmount, type Money } from '@skoka/core';
import type {
  AcceptedAllocation, AcceptedPlan, Account, Amount, Consent, CreditTerms, FocusKind,
  Goal, IncomeStream,
  InsightDisplayState, Loan,
  Obligation, IncomeExpectation, PlainDate, PlanMode, PlannedAllocation, PlannedExpense,
  BufferMove, PeriodBaseline, PeriodDeal, PeriodPin, PeriodTopicOff, Reserve, StableBudgetPlan, StablePayoutFact, TradeoffChoice, PeriodChoice,
} from '@skoka/core';
import type { Category, NewCategoryMode } from './categories.js';
import type { GoalEvent, ReceiptImport, Tx } from './store.js';
import { migrateAccountIcon, migrateCategoryIcon } from './icon-migrate.js';

/**
 * Версия схемы снимка.
 *
 * Растёт, когда старый клиент перестаёт понимать новые данные. Сервер не
 * принимает снимок с версией ниже сохранённой — это и есть защита от
 * обеднения (см. `apps/api/src/server.mjs`).
 */
export const SCHEMA_VERSION = 9;

/*
 * Версия 9 (14.10.2026): корректировка остатка.
 *
 * Операция `adjustment` теперь записывается: «на счёте стало столько-то».
 * Её сумма — со знаком (минус — денег стало меньше), а у трат и приходов
 * сумма всегда положительная. Клиент версии 8 таких записей не создавал и
 * читает их неверно: удаление не возвращает остаток, сумма без знака. Там же
 * значок счёта `bank:<id>` — логотип банка, которого клиент 8 не нарисует.
 */

/*
 * Версия 8 (07.10.2026): значки Font Awesome.
 *
 * У категорий и счетов поле `icon` теперь хранит имя иконки Font Awesome, а
 * не имя lucide и не эмодзи. Клиент версии 7 таких имён не знает и показал бы
 * вместо значка текст «cart-shopping». Старые снимки при чтении переводятся
 * на новые имена — `icon-migrate.ts`.
 */

/*
 * Версия 4 (21.09.2026): модель дохода.
 *
 * Подняли не ради нового поля, а ради **изменившегося смысла**. У ожидания
 * появились состояния `partial` и `missed`, и клиент версии 3 читает их
 * неверно: частично полученный приход он считает закрытым — то есть показывает
 * человеку, что денег ждать больше нечего, и может записать поверх. Новые
 * поля сами по себе пережили бы старого клиента (незнакомое он сохраняет как
 * есть), а вот незнакомое **значение** известного поля — нет.
 */

/*
 * Версия 5 (21.09.2026): стабильный бюджет.
 *
 * Причина та же и опять не в новых списках. У резерва появился вид
 * `forKind: 'stable'` — буфер, из которого человек платит себе. Клиент
 * версии 4 такого вида не знает: он покажет эти деньги «бронью», а в
 * конвертах счёта разрешит снять их одним нажатием — то есть разобрать
 * буфер, не понимая, что разбирает.
 */

/** Факты и решения человека. Всё, что должно пережить переустановку. */
export interface PersistedState {
  accounts: Account[];
  creditTerms: CreditTerms[];
  loans: Loan[];
  incomeStreams: IncomeStream[];
  obligations: Obligation[];
  /** Подтверждён ответ об обязательствах, включая их отсутствие. */
  obligationsKnown?: boolean;
  goals: Goal[];
  goalEvents: GoalEvent[];
  plannedExpenses: PlannedExpense[];
  /**
   * Назначения будущих денег. Не конверты: деньги ещё не пришли.
   *
   * Ездят в снимке, потому что это решение человека, а не расчёт: на другом
   * устройстве он должен увидеть тот же обещанный приход, а не пустой прогноз.
   */
  plannedAllocations: PlannedAllocation[];
  /**
   * Разовые приходы, которых ждут.
   *
   * Ездят в снимке: это сказанное человеком, а не расчёт. На другом устройстве
   * он должен увидеть тот же обещанный возврат долга — и тот же вопрос «пришло
   * ли», пока приход не подтверждён.
   */
  expectedIncomes: IncomeExpectation[];
  reserves: Reserve[];
  /**
   * Планы стабильного бюджета и состоявшиеся выплаты себе.
   *
   * Ездят в снимке: это договор человека с самим собой и история его
   * исполнения. На другом устройстве он должен увидеть ту же дату следующей
   * выплаты, а не пустой экран с предложением включить режим заново.
   */
  stablePlans: StableBudgetPlan[];
  stablePayouts: StablePayoutFact[];
  /** История буфера: без неё месяц не отличит проеденный запас от пополненного. */
  stableMoves: BufferMove[];
  transactions: Tx[];
  /**
   * Загруженные электронные чеки — опознание документа, а не сам файл.
   *
   * Ездит в снимке затем, что переустановка приложения не должна превращать
   * уже учтённый чек в новую покупку. Сам документ остаётся на устройстве:
   * он тяжёлый, а для опознания довольно хеша и реквизитов.
   */
  receiptImports: ReceiptImport[];
  categories: Category[];
  categoryDefaultsReady?: boolean;
  acceptedPlans: AcceptedPlan[];
  /**
   * Какой доход задаёт периоды аналитики и с какого дня.
   *
   * Хранится выбор, а не границы: границы выводятся из записанных приходов,
   * и правка операции их пересчитывает. Без выбора новый крупный доход
   * перекроил бы идущий период и всю историю. Снимок старого клиента поля
   * не имеет — список читается пустым, и выбор делается заново.
   */
  periodPins: PeriodPin[];
  /**
   * Основы сравнения периодов аналитики: суммы плана на начало периода.
   *
   * Отдельно от `acceptedPlans` нарочно: старый клиент выбирает месячный
   * план по совпадению дат, и основа календарного периода с теми же датами
   * подменила бы ему месячную версию. Незнакомое поле он просто сохраняет.
   */
  periodBaselines: PeriodBaseline[];
  /**
   * Подсказки, которые человек закрыл кнопкой «Понятно».
   *
   * Ездят в снимке: это его ответ, а не настройка устройства, и на другом
   * телефоне та же подсказка не должна появиться заново.
   */
  dismissedHints: string[];
  /**
   * Разбор прошлого периода: договорённости («Попробую»), темы, которые
   * человек попросил не поднимать («это моё решение»), и периоды, разбор
   * которых отложен «Не сейчас». Ездят в снимке: это ответы человека, и
   * предел «две договорённости» держится на всех устройствах.
   */
  periodDeals: PeriodDeal[];
  periodTopicsOff: PeriodTopicOff[];
  periodReviewLater: string[];
  /**
   * Части, где «обычно в месяц» — закреплённое число человека, а не история
   * трат (`need`, `want`). Решение человека — ездит в снимке.
   */
  usualPinned: string[];
  /**
   * Порядок групп счетов («Карты», «Наличные»…), который расставил человек.
   * Порядок счетов внутри группы — в самих счетах (`order`).
   */
  accountGroups: string[];
  /**
   * Принятые оперативные раскладки: история решений о сегодняшних деньгах.
   *
   * Ездят в снимке целиком: это подтверждения человека, и на другом
   * устройстве он должен увидеть ту же действующую версию, а не пересчёт.
   */
  allocations: AcceptedAllocation[];
  /**
   * Согласия человека. Ездят в снимке, потому что это его подтверждения, а не
   * настройка устройства: на новом телефоне спрашивать заново нечего.
   */
  consents: Consent[];
  /**
   * Что из наблюдений человек уже видел и отложил.
   *
   * Ездит в снимке, потому что это его решение, а не состояние устройства:
   * отложенное на телефоне не должно всплывать на компьютере заново.
   */
  insightLog: InsightDisplayState[];
  /**
   * Обычные траты со статусом известности.
   *
   * Не число: «не знаю» и подтверждённый ноль — разные факты, и подставлять
   * вместо первого второй значит выдумывать за человека ответ.
   */
  needBaseline: Amount;
  wantBaseline: Amount;
  /**
   * Как человека зовут. Нужно ровно для одного: подписать кнопку профиля.
   *
   * Необязательно и спрашивается один раз. Пока имени нет, в шапке стоит
   * нейтральный значок — выдумывать инициалы из почты нельзя: «al» из
   * `aleksey.petrov@…` может оказаться чужим именем на общем устройстве.
   */
  profileName?: string;
  /**
   * С какого дня приложение ведёт учёт.
   *
   * Граница добросовестности: автоматически найденную просрочку раньше этой
   * даты вычитать нельзя. Человек, заведший аккаунт 18 сентября, не должен
   * получить три месяца выдуманной задолженности по платежу, который он всё
   * это время исправно платил мимо приложения.
   *
   * Ставится один раз при завершении настройки. Для тех, кто настроился
   * раньше её появления, выводится из самой поздней даты актуальности
   * остатков — она и есть день, когда человек сверил картину с реальностью.
   */
  trackingStartedAt?: PlainDate;
  focusOverride?: FocusKind;
  /** Насколько агрессивно человек идёт к целям. Раньше была константа в коде. */
  planMode?: PlanMode;
  /** Выбор между резервом и гашением: он участвует в распределении. */
  tradeoffChoice?: TradeoffChoice;
  /**
   * Как поступать с покупкой из чека или выписки, которой нет подходящей
   * категории: создавать новую, спрашивать или класть в «Прочее». Пусто —
   * человек ещё не отвечал, и его спросят при первом таком случае.
   */
  newCategoryMode?: NewCategoryMode;
  /** Откуда считать период: с 1-го числа или от прихода дохода. Пусто — сам. */
  periodChoice?: PeriodChoice;
}

/** Вычисляемое и сеансовое. В снимок не попадает никогда. */
export interface RuntimeState {
  today: string;
  isEmpty: boolean;
}

export interface Snapshot {
  schemaVersion: number;
  savedAt: string;
  state: PersistedState;
}

/**
 * Что пошло не так при чтении снимка.
 *
 * Разделение принципиальное. `errors` — испорченная мелочь: её можно пережить,
 * заменив значением по умолчанию. `fatal` — снимок, которому нельзя верить
 * целиком; принять его значило бы стереть данные человека в обмен на мусор.
 */
export interface Problems {
  errors: string[];
  fatal: string[];
}

interface Codec<T> {
  /** Что писать в снимок. */
  encode: (value: T) => unknown;
  /** Как прочитать. Отсутствие и мусор дают значение по умолчанию. */
  decode: (raw: unknown, problems: Problems, field: string) => T;
}

/**
 * Список записей с проверкой каждой.
 *
 * Проверять только тип поля мало: снимок со счётом `{balance:'oops'}` прежде
 * считался годным и замещал настоящие счета. Балансы после этого складывались
 * со строкой, и человек видел вместо своих денег `NaN` — или ноль.
 *
 * `shape` описывает, что делает запись пригодной. Чужие поля в записи
 * допустимы: их мог добавить более новый клиент, и выбрасывать их не за что.
 */
const list = <T>(shape?: (row: Record<string, unknown>) => string | null): Codec<T[]> => ({
  encode: (v) => v,
  decode: (raw, problems, field) => {
    if (raw === undefined || raw === null) return [];
    if (!Array.isArray(raw)) {
      // Не список там, где обязан быть список, — это не битая запись, а битый
      // снимок. Прочитать его как пустой значило бы стереть все счета
      // человека и назвать это успешной загрузкой.
      problems.fatal.push(`${field}: ожидал список, получил ${typeof raw}`);
      return [];
    }
    if (shape) {
      for (const [i, row] of raw.entries()) {
        if (typeof row !== 'object' || row === null || Array.isArray(row)) {
          problems.fatal.push(`${field}[${i}]: запись не объект`);
          continue;
        }
        const wrong = shape(row as Record<string, unknown>);
        if (wrong) problems.fatal.push(`${field}[${i}]: ${wrong}`);
      }
    }
    return raw as T[];
  },
});

/** Проверки полей записи: строгие ровно там, где на поле опираются расчёты. */
const id = (row: Record<string, unknown>): string | null =>
  typeof row.id === 'string' && row.id ? null : 'нет идентификатора';
const money = (key: string) => (row: Record<string, unknown>): string | null =>
  typeof row[key] === 'number' && Number.isFinite(row[key]) ? null : `${key}: не сумма`;
const all = (...checks: ((row: Record<string, unknown>) => string | null)[]) =>
  (row: Record<string, unknown>): string | null => {
    for (const check of checks) { const wrong = check(row); if (wrong) return wrong; }
    return null;
  };

const moneyValue = (): Codec<Money> => ({
  encode: (v) => v,
  decode: (raw, problems, field) => {
    if (raw === undefined || raw === null) return ZERO;
    if (typeof raw !== 'number' || !Number.isFinite(raw)) {
      problems.errors.push(`${field}: ожидал сумму в копейках, получил ${typeof raw}`);
      return ZERO;
    }
    // Копейки целые по построению: дробь означала бы потерю точности.
    return Math.round(raw) as Money;
  },
});

/**
 * Значение со статусом известности.
 *
 * Старые снимки хранят голое число: оно читается как подтверждённое. Задним
 * числом объявить, что человек чего-то не знал, нельзя — как и наоборот.
 */
const amount = (): Codec<Amount> => ({
  encode: (v) => v,
  decode: (raw, problems, field) => {
    if (raw === null || raw === undefined) return unknownAmount('not_asked');
    if (typeof raw === 'number') return amountFromLegacy(Math.round(raw) as Money);
    if (typeof raw === 'object' && 'known' in (raw as Record<string, unknown>)) {
      const a = raw as Amount;
      if (a.known === true && typeof a.value === 'number') return confirmed(Math.round(a.value) as Money);
      if (a.known === false) return a;
    }
    problems.errors.push(`${field}: не похоже ни на сумму, ни на «не знаю»`);
    return unknownAmount('not_asked');
  },
});

const optional = <T>(): Codec<T | undefined> => ({
  encode: (v) => v,
  decode: (raw) => (raw === null || raw === undefined ? undefined : raw as T),
});

const optionalBoolean = (): Codec<boolean | undefined> => ({
  encode: (v) => v,
  decode: (raw, problems, field) => {
    if (raw === undefined || raw === null) return undefined;
    if (typeof raw === 'boolean') return raw;
    problems.errors.push(`${field}: ожидал логическое значение, получил ${typeof raw}`);
    return undefined;
  },
});

const optionalString = <T extends string>(): Codec<T | undefined> => ({
  encode: (v) => v,
  decode: (raw, problems, field) => {
    if (raw === undefined || raw === null) return undefined;
    if (typeof raw !== 'string') {
      problems.errors.push(`${field}: ожидал строку, получил ${typeof raw}`);
      return undefined;
    }
    return raw as T;
  },
});

/**
 * Кодек на каждое поле состояния.
 *
 * Тип `{ [K in keyof PersistedState]-?: … }` требует все ключи, включая
 * необязательные: добавили поле в состояние и не добавили сюда — сборка
 * падает, а не данные.
 */
const CODECS: { [K in keyof PersistedState]-?: Codec<PersistedState[K]> } = {
  accounts: list<Account>(all(id, money('balance'))),
  creditTerms: list<CreditTerms>(money('limit')),
  loans: list<Loan>(all(id, money('principal'))),
  incomeStreams: list<IncomeStream>(all(id, money('amount'))),
  obligations: list<Obligation>(all(id, money('amount'))),
  obligationsKnown: optionalBoolean(),
  goals: list<Goal>(all(id, money('targetAmount'))),
  goalEvents: list<GoalEvent>(),
  plannedExpenses: list<PlannedExpense>(all(id, money('amount'))),
  plannedAllocations: list<PlannedAllocation>(all(id, money('amount'))),
  expectedIncomes: list<IncomeExpectation>(all(id, money('amount'))),
  reserves: list<Reserve>(all(id, money('amount'))),
  stablePlans: list<StableBudgetPlan>(all(id, money('payout'))),
  stablePayouts: list<StablePayoutFact>(all(id, money('amount'))),
  stableMoves: list<BufferMove>(all(id, money('amount'))),
  transactions: list<Tx>(all(id, money('amount'))),
  receiptImports: list<ReceiptImport>(id),
  categories: list<Category>(id),
  categoryDefaultsReady: optional<boolean>(),
  acceptedPlans: list<AcceptedPlan>(id),
  periodPins: list<PeriodPin>(),
  periodBaselines: list<PeriodBaseline>(),
  dismissedHints: list<string>(),
  periodDeals: list<PeriodDeal>(),
  periodTopicsOff: list<PeriodTopicOff>(),
  periodReviewLater: list<string>(),
  usualPinned: list<string>(),
  accountGroups: list<string>(),
  allocations: list<AcceptedAllocation>(id),
  consents: list<Consent>(id),
  insightLog: list<InsightDisplayState>(),
  needBaseline: amount(),
  wantBaseline: amount(),
  profileName: optionalString<string>(),
  trackingStartedAt: optionalString<PlainDate>(),
  focusOverride: optionalString<FocusKind>(),
  planMode: optionalString<PlanMode>(),
  tradeoffChoice: optional<TradeoffChoice>(),
  newCategoryMode: optionalString<NewCategoryMode>(),
  periodChoice: optional<PeriodChoice>(),
};

const KEYS = Object.keys(CODECS) as (keyof PersistedState)[];

/**
 * Снимок из состояния.
 *
 * `unknown` — поля, которых эта версия клиента не знает: они пришли из снимка,
 * записанного более новым клиентом. Они возвращаются **на своё место внутри
 * состояния**, а не в отдельный угол конверта: на другом устройстве их читает
 * тот самый новый клиент, и он ищет их там, где положил. Иначе телефон,
 * отставший на одну версию, молча обеднял бы данные при каждой синхронизации.
 */
export function encode(state: PersistedState, unknown?: Record<string, unknown>): Snapshot {
  // Чужие поля кладутся первыми: своё мы знаем точно и перетираем им.
  const out: Record<string, unknown> = { ...(unknown ?? {}) };
  for (const key of KEYS) {
    const value = (CODECS[key] as Codec<unknown>).encode(state[key]);
    // Необязательное поле без значения в снимок не пишем: `undefined` в JSON
    // превратился бы в `null` и стал бы отличаться от «поля не было».
    if (value !== undefined) out[key] = value;
  }
  return {
    schemaVersion: SCHEMA_VERSION,
    savedAt: new Date().toISOString(),
    state: out as unknown as PersistedState,
  };
}

export interface DecodeResult {
  state: PersistedState;
  /** Поля, которых эта версия не знает: сохраняются и возвращаются при записи. */
  unknown: Record<string, unknown>;
  /** Что не удалось прочитать. Снимок с одной битой записью не обнуляет всё. */
  errors: string[];
  /**
   * Почему снимок нельзя применять вовсе.
   *
   * Пустой список — снимок годен. Непустой означает, что локальные данные
   * трогать нельзя: они заведомо лучше того, что пришло.
   */
  fatal: string[];
  schemaVersion: number;
}

export function decode(raw: unknown): DecodeResult {
  const problems: Problems = { errors: [], fatal: [] };
  const envelope = (raw ?? {}) as Record<string, unknown>;
  // Снимки до введения версии лежат плоско, без конверта.
  const body = (typeof envelope.state === 'object' && envelope.state !== null
    ? envelope.state
    : envelope) as Record<string, unknown>;

  const version = typeof envelope.schemaVersion === 'number' ? envelope.schemaVersion : 1;

  // Конверт со своей версией обязан нести состояние объектом. `state: null`
  // прежде читался как «снимок без полей» — то есть как приказ всё стереть,
  // и данные человека заменялись пустотой при полном молчании.
  if (envelope.schemaVersion !== undefined
    && (typeof envelope.state !== 'object' || envelope.state === null || Array.isArray(envelope.state))) {
    problems.fatal.push('снимок объявил версию, но состояния в нём нет');
  }
  // Снимок новее, чем эта сборка умеет читать. Записать его обратно значило бы
  // выбросить всё, чего мы не понимаем, — а понимает ли его другое устройство,
  // мы не знаем. Поэтому не читаем и не трогаем локальное.
  if (version > SCHEMA_VERSION) {
    problems.fatal.push(
      `снимок версии ${version} новее, чем понимает это приложение (${SCHEMA_VERSION}) — обнови приложение`,
    );
  }

  const state = {} as PersistedState;
  const target = state as unknown as Record<string, unknown>;
  for (const key of KEYS) {
    const value = (CODECS[key] as Codec<unknown>).decode(body[key], problems, String(key));
    if (value !== undefined) target[key] = value;
  }

  // Значки снимков до версии 8 — имена lucide и эмодзи.
  if (state.categories) state.categories = state.categories.map(migrateCategoryIcon);
  if (state.accounts) state.accounts = state.accounts.map(migrateAccountIcon);

  const known = new Set<string>(KEYS as string[]);
  const unknown: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(body)) if (!known.has(k)) unknown[k] = v;

  return { state, unknown, errors: problems.errors, fatal: problems.fatal, schemaVersion: version };
}
